import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';

import { CircularProgress, Divider } from '@mui/material';
import styled from 'styled-components';
import { ModalCloseIcon, TickCircle } from '@src/components/Icons';
import LabeledTextField from '@src/components/CustomTextField/LabeledTextField';
import CustomButton from '@src/components/CustomButton/CustomButton';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import { CustomAlertType } from '@src/interfaces/type';
import { validateEmail } from '@src/helpers/Helpers';
import { MAX_PHONE_NUMBER_LENGTH, MIN_PHONE_NUMBER_LENGTH } from '@src/Constants';

const StyledHeader = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	background: '#FFF9E6',
	height: '56px',
	borderBottom: '1.5px solid #FFBE00',
	width: '100%',
	paddingLeft: '10px',
	paddingRight: '10px',
});

const StyledPara = styled.p({
	fontSize: '16px',
	fontWeight: '600',
	color: '#333333',
	textAlign: 'center',
	span: {
		color: '#FFBE00',
	},
});

const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 14px;
	width: 100%;
	height: 68px;
	padding: 20px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
`;

interface Props {
	open: boolean;
	setOpen: (value: boolean) => void;
	style?: React.CSSProperties | {};
	setAlert: (data: CustomAlertType) => void;
	displayCustomError: any;
}

const modalStyling = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	outline: 'none !important',
	maxWidth: '324px',
	maxHeight: 'auto',
	backgroundColor: 'background.paper',
	borderRadius: '4px',
	boxShadow: '24px',
	letterSpacing: '0.28px',
};

export default function ErpModal({ open, setOpen, style, setAlert, displayCustomError }: Props) {
	const { apiService }: any = React.useContext(ApplicationContext);
	const [user, setUser] = React.useState({
		name: '',
		phoneNumber: '',
		email: '',
	});
	const [loading, setLoading] = React.useState(false);
	const [step, setStep] = React.useState('');
	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);
	const mobileStyle = {
		...modalStyling,
		...style,
	};

	const isButtonDisable = () => {
		return (
			!user?.name ||
			!user?.email ||
			!(
				user?.phoneNumber?.length >= MIN_PHONE_NUMBER_LENGTH &&
				user?.phoneNumber?.length <= MAX_PHONE_NUMBER_LENGTH
			) ||
			loading
		);
	};

	const clearFields = () => {
		setUser({
			name: '',
			phoneNumber: '',
			email: '',
		});
	};

	const handleDone = () => {
		const emailValidation = validateEmail(user?.email);
		if (emailValidation) {
			setLoading(true);
			const data = {
				name: user?.name,
				phoneNumber: user?.phoneNumber,
				email: user?.email,
			};
			apiService?.connectERP(
				data,
				(res: AxiosResponse) => {
					setLoading(false);
					clearFields();
					setStep('success');
				},
				(error: AxiosError) => {
					displayCustomError('Something went wrong!');
					setLoading(false);
				}
			);
		} else {
			displayCustomError('Invalid Email Format');
		}
	};

	const erpSteps = () => {
		if (step === 'success') {
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: '20px',
						padding: '24px',
					}}
				>
					<TickCircle />
					<StyledPara>
						Thanks! Someone from <span>Cheeni</span> will get in touch with you soon.
					</StyledPara>
				</Box>
			);
		}
		return (
			<>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						padding: '14px',
						gap: '14px',
						width: '100%',
					}}
				>
					<LabeledTextField
						label="Contact Name"
						value={user?.name}
						onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
							setUser({ ...user, name: e?.target?.value as string })
						}
						placeholder="eg. Daniel"
						styles={{ width: '292px', height: '36px' }}
					/>
					<LabeledTextField
						label="Phone Number"
						type="phoneNumber"
						value={user?.phoneNumber}
						onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
							setUser({ ...user, phoneNumber: e?.target?.value as string })
						}
						placeholder="91-233---"
						styles={{ width: '292px', height: '36px' }}
					/>
					<LabeledTextField
						label="Email Address"
						type="email"
						marginLeft="8px"
						value={user?.email}
						onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
							const enteredEmail = e?.target?.value as string;
							setUser({ ...user, email: enteredEmail });
						}}
						placeholder="e.g name@gmail.com"
						styles={{ width: '292px', height: '36px' }}
					/>
				</Box>
				<StyledBottomBtnContainer>
					<CustomButton
						type="submit"
						title={
							loading ? (
								<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
							) : (
								'Done'
							)
						}
						handleClick={() => handleDone()}
						disabled={isButtonDisable()}
						styles={{
							width: '132px',
							height: '36px',
						}}
					/>
				</StyledBottomBtnContainer>
			</>
		);
	};

	return (
		<Modal
			open={open}
			onClose={() => {
				handleClose();
			}}
			aria-labelledby="modal-modal-title"
			aria-describedby="modal-modal-description"
		>
			<Box
				sx={{
					...modalStyling,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '16px',
				}}
			>
				<StyledHeader>
					<Typography variant="body1">ERP Connect</Typography>
					<Box sx={{ cursor: 'pointer' }} onClick={handleClose}>
						<ModalCloseIcon />
					</Box>
				</StyledHeader>
				{erpSteps()}
			</Box>
		</Modal>
	);
}
